import { useEffect } from "react";

const SITE_ORIGIN = "https://stranding.cn";

export interface SeoOptions {
  title: string;
  description: string;
  keywords?: string;
  path: string;
  image?: string;
  type?: string;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

function setMeta(attr: "name" | "property", key: string, content: string) {
  const selector = `meta[${attr}="${key}"]`;
  let tag = document.head.querySelector(selector) as HTMLMetaElement | null;
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute(attr, key);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
}

/**
 * 统一管理页面 TDK、Open Graph、Twitter Card、Canonical 与 JSON-LD 结构化数据。
 */
export function useSeo(options: SeoOptions) {
  const { title, description, keywords, path, image, type = "website", jsonLd } = options;
  const jsonLdKey = jsonLd ? JSON.stringify(jsonLd) : "";

  useEffect(() => {
    document.title = title;
    setMeta("name", "description", description);
    if (keywords) {
      setMeta("name", "keywords", keywords);
    }
    setMeta("name", "robots", "index, follow, max-image-preview:large");

    setMeta("property", "og:title", title);
    setMeta("property", "og:description", description);
    setMeta("property", "og:type", type);
    setMeta("property", "og:url", `${SITE_ORIGIN}${path}`);
    setMeta("property", "og:site_name", "瓜田黑料");
    setMeta("property", "og:locale", "zh_CN");
    if (image) {
      setMeta("property", "og:image", image);
    }

    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", title);
    setMeta("name", "twitter:description", description);
    if (image) {
      setMeta("name", "twitter:image", image);
    }

    let link = document.head.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      document.head.appendChild(link);
    }
    link.setAttribute("href", `${SITE_ORIGIN}${path}`);

    document.querySelectorAll('script[data-seo-jsonld="true"]').forEach((node) => node.remove());
    if (jsonLdKey) {
      const parsed = JSON.parse(jsonLdKey) as Record<string, unknown> | Record<string, unknown>[];
      const blocks = Array.isArray(parsed) ? parsed : [parsed];
      blocks.forEach((block) => {
        const script = document.createElement("script");
        script.type = "application/ld+json";
        script.setAttribute("data-seo-jsonld", "true");
        script.text = JSON.stringify(block);
        document.head.appendChild(script);
      });
    }
  }, [title, description, keywords, path, image, type, jsonLdKey]);
}